import { isNil, isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import {
  isCustomerWebViewEnabled as getIsCustomerWebViewEnabled,
  isEmbeddedInIFrame as getIsEmbeddedInIFrame,
  isSalesforceRep as getIsSalesforceRep,
} from 'src/redux/modules/authentication/authentication.module.selectors';
import { moduleActions } from 'src/redux/modules';
import { useActions } from 'src/redux/utils/hooks';
import { getSelectedBank } from 'src/redux/modules/bankSelection/bankSelection.selectors';
import {
  getEligibleBanks,
  getIsSingleBankWithDisabledStatus,
} from 'src/redux/modules/existingBanks/existingBanks.selectors';
import FundList from '../../Buy/BuyFlow/FundList';
import {
  AUTOMATIC_EDIT_NO_CHANGES_MADE,
  CANCEL_TRADE_CONFIRMATION_MODAL,
  IRA_AUTO_MAX_TOO_MANY_DAYS_MODAL,
} from 'src/redux/modules/modals/modals.module';
import { getModals } from 'src/redux/modules/modals/modals.selectors';
import { getProductTypes } from 'src/redux/modules/productTypes/productType.selectors';
import { dispatchPageLoad } from 'src/redux/utils/analytics-helper';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import FundingSource from '../../FundingSource/FundingSource/FundingSource';
import { isDisabled as bankIsDisabled } from '../..//FundingSource/BankSelect/utils';
import ProgressStep from '../../ProgressStep/ProgressStep';
import AppTitle from '../../Shared/AppTitle/AppTitle';
import './IRAAutoMaxSteps.scss';
import IRAAutoMaxReview from '../IRAAutoMaxReview/IRAAutoMaxReview';
import {
  didUserSelectSameDays,
  didUserSelectSameMonths,
  getContributionPerAccountMeetsMinimum,
  getIRASelectedDayCount,
} from 'src/redux/modules/funds/IRAAutoMaxCartDetails/IRAAutoMaxCartDetails.selectors';
import { getCalculation } from '../../../redux/modules/funds/IRAAutoMaxCalculation';
import { getIRAAutoMaxCalculationLoaded } from '../../../redux/modules/funds/IRAAutoMaxCalculation/IRAAutoMaxCalculations.selectors';
import Loader from '../../Shared/Loader/Loader';
import { getCalculationPayload } from '../../../redux/modules/funds/IRAAutoMaxCalculation/IRAAutoMaxCalculations.selectors';
import { getIRAAutoMaxSubmissionPayload } from '../../../redux/modules/funds/IRAAutoMaxSubmission/IRAAutoMaxSubmission.selectors';
import {
  submitIRAAutoMax,
  editIRAAutoMax,
} from '../../../redux/modules/funds/IRAAutoMaxSubmission';
import {
  getAutoMaxFunds,
  hasStopTransferFunds,
  getOwnedEnabledIRAAutoMaxFunds,
  userSelectedIRAAutoMaxFundsSameAsExisting,
  getIRAAutoMaxFirstOwnedFund,
  getIRAAutoMaxCurrentBank,
} from '../../../redux/modules/shoppingCart/shoppingCart.selectors';

import { selectBank } from '../../../redux/modules/bankSelection/bankSelection.module';
import {
  setIRAAutoMaxMonths,
  setIRAAutoMaxDays,
} from '../../../redux//modules/funds/IRAAutoMaxCartDetails/IRAAutoMaxCartDetails.module';
import { selectFund } from '../../../redux/modules/shoppingCart/shoppingCart.module';
import { getAllMonthsAndDays } from '../../../redux/modules/automaticInvestments/automaticInvestments.selectors';

const marqueeSubtitle = (
  <span>
    Automatically maximize your IRA contributions among your T. Rowe Price
    Investments.
  </span>
);

const marqueeTitle = 'IRA AutoMax';

const monthArr = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

export const IRAAutoMaxSteps = () => {
  const history = useHistory();
  const location = useLocation();
  const [userAgrees, setUserAgrees] = useState(false);
  const hasIRAAutoMax = useSelector(getAutoMaxFunds);
  const stopTransfer = useSelector(hasStopTransferFunds);

  const ownedEnabledFunds = useSelector(
    getOwnedEnabledIRAAutoMaxFunds,
    isEqual
  );

  const firstFund = useSelector(getIRAAutoMaxFirstOwnedFund);
  const currentBank = useSelector(getIRAAutoMaxCurrentBank);

  const toggleTerms = () => {
    setUserAgrees(!userAgrees);
  };

  const actions = useActions(moduleActions);
  const { setProgress, openModal } = actions;

  const onClickScroll = (step, element) => {
    setProgress(step);
    scrollElementIntoFullView(element, 15);
  };
  const selectedFreq = useSelector(getAllMonthsAndDays);

  const {
    isIFrame,
    isSalesforceRep,
    availableEditFund,
    agreementMap,
    currentStep,
    customerWebViewEnabled,
    editModeBank,
    eligibleBanks,
    existingBanks: { fetchingAccounts: banksLoading, error, isLoaded },
    isSingleBankWithDisabledStatus,
    selectedBank,
    meetsMinimum,
  } = useSelector((state) => {
    const {
      editModeBank,
      existingBanks,
      progress: { currentStep, previousStep },
    } = state;
    return {
      currentStep,
      customerWebViewEnabled: getIsCustomerWebViewEnabled(state),
      editModeBank,
      eligibleBanks: getEligibleBanks(state),
      existingBanks,
      isIFrame: getIsEmbeddedInIFrame(state),
      isSalesforceRep: getIsSalesforceRep(state),
      isSingleBankWithDisabledStatus: getIsSingleBankWithDisabledStatus(state),
      modals: getModals(state),
      previousStep,
      productTypes: getProductTypes(state),
      selectedBank: getSelectedBank(state),
      meetsMinimum: getContributionPerAccountMeetsMinimum(state),
    };
  }, shallowEqual);

  const calculationLoaded = useSelector(getIRAAutoMaxCalculationLoaded);
  const calculationPayload = useSelector(getCalculationPayload, isEqual);
  const selectedDaysCount = useSelector(getIRASelectedDayCount);
  const selectedSameDays = useSelector(didUserSelectSameDays);
  const selectedSameMonths = useSelector(didUserSelectSameMonths);
  const currFundsEqualPrevFunds = useSelector(
    userSelectedIRAAutoMaxFundsSameAsExisting
  );
  // only re-calculate if current step is 3
  const iraAutoMaxSubmissionPayload = useSelector(
    getIRAAutoMaxSubmissionPayload,
    () => currentStep !== 3
  );

  const onSubmitButtonClick = () => {
    dispatch(
      hasIRAAutoMax.length > 0
        ? editIRAAutoMax(iraAutoMaxSubmissionPayload)
        : submitIRAAutoMax(iraAutoMaxSubmissionPayload)
    );
    window.scrollTo(0, 0);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (currentStep === 3) {
      dispatch(getCalculation(calculationPayload));
    }
  }, [dispatch, currentStep, calculationPayload]);

  /**
   * EDIT  FLOW: preselect currentBank
   */
  useEffect(() => {
    if (currentBank && !bankIsDisabled({ agreementMap }, currentBank)) {
      dispatch(selectBank(currentBank));
    }
  }, [agreementMap, currentBank, dispatch]);

  /**
   * EDIT: preselect selected funds
   */
  useEffect(() => {
    if (ownedEnabledFunds?.length) {
      ownedEnabledFunds.forEach((fund) =>
        dispatch(selectFund(fund.agreementID, fund, fund.BPID))
      );
    }
  }, [dispatch, ownedEnabledFunds]);

  /**
   * EDIT: preselect selected months/days
   */
  useEffect(() => {
    if (selectedFreq?.selectedDays && selectedFreq?.selectedMonths) {
      dispatch(
        setIRAAutoMaxDays(
          selectedFreq.selectedDays.map((value) => ({
            label: value?.toString() ? value?.toString() : '',
            value,
          }))
        )
      );
      dispatch(
        setIRAAutoMaxMonths(
          selectedFreq.selectedMonths.map((value) => ({
            label: monthArr[value],
            value,
          }))
        )
      );
    }
  }, [dispatch, selectedFreq]);

  // if pending redirect to ATLP US429845

  if (firstFund?.pendingTrade) {
    history.push(`/usis/transactions/automatic/${window.location.search}`);
    return <></>;
  }

  // if on create and existing automax redirect to edit
  if (hasIRAAutoMax.length > 0 && !/edit/.test(location.pathname)) {
    history.push(
      `/usis/transactions/automatic/ira-automax/edit${window.location.search}`
    );
    return <></>;
  }

  // if on edit and no existing automax redirect to create
  if (!hasIRAAutoMax.length && /edit/.test(location.pathname)) {
    history.push(
      `/usis/transactions/automatic/ira-automax/${window.location.search}`
    );
    return <></>;
  }

  return (
    <form className="animated fadeInUp" onSubmit={(e) => e.preventDefault()}>
      {!(isIFrame || (!customerWebViewEnabled && isSalesforceRep)) && (
        <AppTitle
          mboxSubtitle=""
          mboxTitle=""
          subtitle={marqueeSubtitle}
          title={marqueeTitle}
        />
      )}

      <div data-testid="bank-selection">
        {/* STEP 1 */}
        <ProgressStep
          expanded
          showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
          OnClickHeader={() => {
            onClickScroll(1, 'funding-target-step');
          }}
          active={currentStep === 1}
          className="funding-target-step"
          clickable={currentStep > 1}
          complete={currentStep > 1}
          nextButtonDisabled={
            isNil(selectedBank) || isSingleBankWithDisabledStatus
          }
          nextButtonLabel={
            <span>
              <b>NEXT:</b> Amounts & Dates
            </span>
          }
          step={1}
          title={
            <span>
              Choose<span className="hidden-xs hidden-sm"> your </span> funding
              source
            </span>
          }
          onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
          onNextClick={() => {
            onClickScroll(2, 'funding-target-step');
          }}
        >
          <FundingSource
            isSalesforceRep={isSalesforceRep}
            customerWebViewEnabled={customerWebViewEnabled}
            actions={actions}
            active={currentStep === 1}
            agreementMap={agreementMap}
            automaticEditFund={availableEditFund}
            completed={currentStep > 1}
            dispatchPageLoad={dispatchPageLoad}
            editModeBank={editModeBank}
            error={error}
            fundingSource={eligibleBanks}
            isLoaded={isLoaded}
            isLoading={banksLoading}
            openDrawer={() => {
              onClickScroll(1, 'funding-target-step');
            }}
            selectedBank={selectedBank}
            stopTransfer={stopTransfer}
          />
        </ProgressStep>
      </div>
      <div data-testid="funds-contribution-schedule">
        {/* STEP 2 */}
        <ProgressStep
          expanded
          showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
          active={currentStep === 2}
          className="schedule-frequency-step"
          clickable={currentStep > 2}
          complete={currentStep > 2}
          nextButtonClassName="e2e-step2-verify-submit"
          nextButtonDisabled={currentStep !== 2 || !meetsMinimum}
          nextButtonLabel={
            <span>
              <b>NEXT:</b> Review Your Plan
            </span>
          }
          step={2}
          title={<span>Select your fund(s) and contribution schedule</span>}
          onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
          OnClickHeader={() => {
            currentStep > 2 ||
              (currentStep !== 2 && onClickScroll(2, 'funding-target-step'));
          }}
          onNextClick={() => {
            if (selectedDaysCount > 4) {
              openModal(IRA_AUTO_MAX_TOO_MANY_DAYS_MODAL);
            } else {
              onClickScroll(3, 'review-step');
            }
          }}
        >
          <FundList
            isRecurring
            clickable={currentStep !== 2}
            currentStep={currentStep}
          />
        </ProgressStep>
      </div>
      <div data-testid="review-submit">
        {/* STEP 3 */}
        <ProgressStep
          showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
          active={currentStep === 3}
          className="review-step"
          clickable={currentStep > 3}
          expanded={currentStep === 3}
          nextButtonDisabled={!userAgrees || customerWebViewEnabled}
          nextButtonLabel="SUBMIT"
          step={3}
          title={<span>Review & submit your plan</span>}
          onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
          OnClickHeader={() => {
            currentStep >= 3 && onClickScroll(3, 'review-step');
          }}
          //ownedEnabledFunds
          onNextClick={() => {
            if (
              hasIRAAutoMax.length > 0 &&
              selectedSameDays &&
              selectedSameMonths &&
              currentBank.accountNumber === selectedBank.accountNumber &&
              currentBank.bankAgreementID === selectedBank.bankAgreementID &&
              currFundsEqualPrevFunds
            ) {
              openModal(AUTOMATIC_EDIT_NO_CHANGES_MADE);
              onClickScroll(2, 'funding-target-step');
            } else {
              onSubmitButtonClick();
            }
          }}
        >
          {calculationLoaded ? (
            <>
              <IRAAutoMaxReview
                toggleTerms={toggleTerms}
                userAgrees={userAgrees}
              />
            </>
          ) : (
            <Loader isVisible />
          )}
        </ProgressStep>
      </div>
    </form>
  );
};

export default IRAAutoMaxSteps;
